import { useToast } from "@/hooks/use-toast";
import { ExpenseFormValues } from "@/schemas/expenseFormSchema";
import { useExpenses } from './useExpenses';
import { Expense, ExpenseCategory } from '@/types/expense';
import { useState, useEffect } from 'react';
import { toast } from "sonner";

export const useTripManagement = () => {
  const { expenses, addExpense, deleteExpense, clearExpenses } = useExpenses();
  const [currentTripName, setCurrentTripName] = useState<string>(() => {
    return localStorage.getItem('currentTripName') || "";
  });
  const [tripHistory, setTripHistory] = useState<any[]>(() => {
    const savedHistory = localStorage.getItem('tripHistory');
    return savedHistory ? JSON.parse(savedHistory) : [];
  });

  // Sync with localStorage whenever state changes
  useEffect(() => {
    localStorage.setItem('currentTripName', currentTripName);
    localStorage.setItem('tripHistory', JSON.stringify(tripHistory));
    console.log("Current trip history:", tripHistory);
  }, [currentTripName, tripHistory]);

  const handleAddExpense = (values: ExpenseFormValues) => {
    console.log("Adding new expense:", values);
    const newExpense = {
      description: values.description,
      amount: Number(values.amount),
      category: values.category as ExpenseCategory,
      date: values.date.toISOString(),
      paidBy: values.paidBy,
      participants: values.participants || []
    };
    
    const expense = addExpense(newExpense);
    console.log("Expense added successfully:", expense);
    
    toast("Expense Added", {
      description: `${values.description} (₹${values.amount}) has been added.`
    });
    
    return expense;
  };

  const handleDeleteExpense = (id: string) => {
    deleteExpense(id);
    toast("Expense Deleted", {
      description: "The expense has been removed from your trip."
    });
  };

  const handleStartNewTrip = (tripName: string) => {
    setCurrentTripName(tripName);
    clearExpenses();
    toast("New Trip Started", {
      description: `Your new trip "${tripName}" has been started.`
    });
  };

  const calculateTripStats = (expenses: Expense[]) => {
    const totalParticipants = new Set(expenses.flatMap(e => e.participants || [])).size;
    const categories = expenses.reduce((acc, exp) => {
      acc[exp.category] = (acc[exp.category] || 0) + Number(exp.amount);
      return acc;
    }, {} as Record<string, number>);
    
    const highestSpender = expenses.reduce((acc, exp) => {
      acc[exp.paidBy] = (acc[exp.paidBy] || 0) + Number(exp.amount);
      return acc;
    }, {} as Record<string, number>);

    return {
      totalParticipants,
      categoryBreakdown: categories,
      topSpender: Object.entries(highestSpender).sort((a, b) => b[1] - a[1])[0]
    };
  };

  const handleEndTrip = () => {
    console.log("Ending trip with name:", currentTripName, "and expenses:", expenses);
    
    if (currentTripName && expenses.length > 0) {
      const stats = calculateTripStats(expenses);
      const completedTrip = {
        name: currentTripName,
        expenses: [...expenses],
        startDate: expenses[0]?.date || new Date().toISOString(),
        endDate: new Date().toISOString(),
        totalAmount: expenses.reduce((sum, exp) => sum + Number(exp.amount), 0),
        stats: {
          totalParticipants: stats.totalParticipants,
          categoryBreakdown: stats.categoryBreakdown,
          topSpender: stats.topSpender
        }
      };
      
      setTripHistory(prev => {
        const newHistory = [...prev, completedTrip];
        localStorage.setItem('tripHistory', JSON.stringify(newHistory));
        return newHistory;
      });
      
      console.log("Trip added to history:", completedTrip);
      
      // Reset current trip data
      setCurrentTripName("");
      clearExpenses();
      localStorage.removeItem('expenses');
      
      toast("Trip Ended", {
        description: `Your trip "${currentTripName}" has been completed and moved to history.`
      });

      // Force a re-render of the history component
      setTimeout(() => {
        window.dispatchEvent(new Event('storage'));
      }, 100);
    } else {
      toast("Cannot End Trip", {
        description: "Please add at least one expense before ending the trip."
      });
    }
  };

  return {
    currentTripName,
    tripHistory,
    expenses,
    handleAddExpense,
    handleDeleteExpense,
    handleStartNewTrip,
    handleEndTrip
  };
};